import React, { useCallback, useEffect, useState } from "react";
import api, { describeAxiosError } from "@/lib/api";
import PartnerLayout from "./PartnerLayout";
import { toast } from "sonner";
import {
    UsersThree,
    Lock,
    Phone,
    WhatsappLogo,
    ArrowRight,
    Sparkle,
    MapPin,
    Briefcase,
    ClockCountdown,
} from "@phosphor-icons/react";

/**
 * PartnerLeadPool — partner-facing CRM lead pool (V2.1.1).
 *
 * Shows pre-CV leads across all workspaces the partner is allowed to
 * see. PII is masked in the list; a click on "Claim" reveals full
 * contact info (subject to daily + active caps enforced server-side).
 */
export default function PartnerLeadPool() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [claiming, setClaiming] = useState(null); // lead_id in-flight
    const [revealed, setRevealed] = useState({}); // lead_id -> full info
    const [filter, setFilter] = useState({ role: "", city: "" });

    const load = useCallback(async () => {
        setLoading(true);
        setErr("");
        try {
            const q = new URLSearchParams();
            if (filter.role) q.set("role_interest", filter.role);
            if (filter.city) q.set("city", filter.city);
            const r = await api.get(`/partner/me/crm-pool?${q.toString()}`);
            setData(r.data);
        } catch (e) {
            setErr(describeAxiosError(e));
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [filter]);

    useEffect(() => { load(); }, [load]);

    const claim = async (lead) => {
        setClaiming(lead.id);
        try {
            const r = await api.post(`/partner/me/crm-pool/${lead.id}/claim`);
            toast.success(`Claimed ${r.data.lead.name} — contact revealed below`);
            setRevealed((s) => ({ ...s, [lead.id]: r.data.lead }));
            // Refresh stats in place.
            setData((d) => d ? {
                ...d,
                claim_stats: r.data.claim_stats,
                rows: d.rows.map((x) => x.id === lead.id ? { ...x, _claimed: true } : x),
            } : d);
        } catch (e) {
            toast.error(e?.response?.data?.detail || describeAxiosError(e));
        } finally {
            setClaiming(null);
        }
    };

    const stats = data?.claim_stats || { today: 0, active: 0 };

    return (
        <PartnerLayout>
            <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="partner-lead-pool">
                <div className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                    <div>
                        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-cobalt mb-1 inline-flex items-center gap-1">
                            <Sparkle size={10} weight="fill" /> Partner CRM pool
                        </p>
                        <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter text-ink">
                            Fresh leads to claim
                        </h1>
                        <p className="text-sm text-[#525252] mt-1">
                            Pre-CV candidates from ad landings & bulk imports.
                            Claim, contact, and submit against your favourite
                            open role for the bounty.
                        </p>
                    </div>
                    <div className="grid grid-cols-2 gap-2 shrink-0">
                        <StatCard label="Claimed today" value={stats.today} />
                        <StatCard label="Active leads" value={stats.active} />
                    </div>
                </div>

                {/* Filters */}
                <div className="mb-6 flex flex-col sm:flex-row gap-2">
                    <input
                        data-testid="partner-lead-filter-role"
                        placeholder="Role (e.g. Sales, Dev)"
                        value={filter.role}
                        onChange={(e) => setFilter({ ...filter, role: e.target.value })}
                        className="border border-[#E5E5E5] bg-white px-3 py-2 text-sm w-full sm:w-64"
                    />
                    <input
                        data-testid="partner-lead-filter-city"
                        placeholder="City"
                        value={filter.city}
                        onChange={(e) => setFilter({ ...filter, city: e.target.value })}
                        className="border border-[#E5E5E5] bg-white px-3 py-2 text-sm w-full sm:w-64"
                    />
                </div>

                {loading && !data && <p className="text-sm text-[#A3A3A3]">Loading pool…</p>}
                {err && <p className="text-sm text-[#DC2626] bg-[#FEF2F2] border border-[#FCA5A5] p-3">{err}</p>}

                {data && data.rows.length === 0 && (
                    <div className="bg-white border border-[#E5E5E5] p-12 text-center">
                        <UsersThree size={40} weight="fill" className="mx-auto text-[#A3A3A3] mb-3" />
                        <p className="text-sm text-[#525252]">
                            No leads matching your filter yet. New leads drop
                            every day — check back tomorrow, or clear the filters.
                        </p>
                    </div>
                )}

                {data && data.rows.length > 0 && (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                        {data.rows.map((r) => {
                            const rev = revealed[r.id];
                            const claimed = !!rev || r._claimed;
                            return (
                                <div
                                    key={r.id}
                                    data-testid={`partner-lead-card-${r.id}`}
                                    className="bg-white border border-[#E5E5E5] p-4 flex flex-col gap-3"
                                >
                                    <div>
                                        <p className="font-bold text-base text-ink">
                                            {rev?.name || r.name_masked}
                                            {r.phone_verified && (
                                                <span
                                                    title="Phone verified via OTP"
                                                    className="ml-2 text-[10px] font-black uppercase tracking-wider px-1.5 py-0.5 bg-[#ECFDF5] text-[#059669] align-middle"
                                                >
                                                    ✓ Verified
                                                </span>
                                            )}
                                        </p>
                                        <p className="text-xs text-[#525252] mt-1 inline-flex items-center gap-2 flex-wrap">
                                            {rev ? (
                                                <span className="inline-flex items-center gap-1 font-bold text-ink">
                                                    <Phone size={10} weight="bold" /> {rev.phone}
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 text-[#A3A3A3]">
                                                    <Lock size={10} weight="bold" /> {r.phone_masked}
                                                </span>
                                            )}
                                            {r.city && (
                                                <span className="inline-flex items-center gap-1">
                                                    <MapPin size={10} weight="bold" /> {r.city}
                                                </span>
                                            )}
                                        </p>
                                        {r.role_interest && (
                                            <p className="text-xs text-[#525252] mt-1 inline-flex items-center gap-1">
                                                <Briefcase size={10} weight="bold" /> {r.role_interest}
                                            </p>
                                        )}
                                        {r.attempt_count > 0 && (
                                            <p className="text-[10px] font-bold uppercase tracking-wider text-[#B45309] mt-1 inline-flex items-center gap-1">
                                                <ClockCountdown size={10} weight="bold" /> {r.attempt_count} prior touches
                                            </p>
                                        )}
                                    </div>

                                    <div className="mt-auto flex items-center gap-2">
                                        {claimed && rev ? (
                                            <>
                                                <a
                                                    href={`tel:${rev.phone}`}
                                                    data-testid={`partner-lead-call-${r.id}`}
                                                    className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 border border-ink bg-white text-ink text-[10px] font-black uppercase tracking-wider hover:bg-ink hover:text-paper transition"
                                                >
                                                    <Phone size={12} weight="bold" /> Call
                                                </a>
                                                <a
                                                    href={`https://wa.me/${(rev.phone || "").replace(/\D/g, "")}`}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    data-testid={`partner-lead-wa-${r.id}`}
                                                    className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 bg-[#25D366] text-white text-[10px] font-black uppercase tracking-wider hover:brightness-95 transition"
                                                >
                                                    <WhatsappLogo size={12} weight="fill" /> WhatsApp
                                                </a>
                                            </>
                                        ) : (
                                            <button
                                                onClick={() => claim(r)}
                                                disabled={claiming === r.id}
                                                data-testid={`partner-lead-claim-${r.id}`}
                                                className="w-full inline-flex items-center justify-center gap-1 px-3 py-2 bg-cobalt text-white text-xs font-black uppercase tracking-wider hover:bg-ink transition disabled:opacity-60"
                                            >
                                                {claiming === r.id ? "Claiming…" : (
                                                    <>Claim & reveal <ArrowRight size={12} weight="bold" /></>
                                                )}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </PartnerLayout>
    );
}

function StatCard({ label, value }) {
    return (
        <div className="bg-white border border-[#E5E5E5] p-3">
            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#525252]">{label}</p>
            <p className="font-heading font-black text-2xl tracking-tighter text-ink mt-1">{value}</p>
        </div>
    );
}
